<template>
  <a v-if="isExternalLink || !props.to" v-bind="$attrs" :href="href" :class="[props.color, decorationClass]">
    <slot />
  </a>
  <RouterLink v-else :to="props.to" v-slot="{ href, navigate }" custom>
    <a v-bind="$attrs" :href="href" :class="[props.color, decorationClass]" @click="navigate">
      <slot />
    </a>
  </RouterLink>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouteLocationRaw, RouterLink } from 'vue-router';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{ to?: RouteLocationRaw; color?: 'inherit' | 'accent'; decoration?: boolean }>(),
  {
    color: 'inherit',
    decoration: true,
  },
);
const decorationClass = computed(() => (props.decoration ? 'decoration' : ''));
const href = computed(() => props.to as string);
// Anything with a URL scheme (http:, tel:, mailto:, blob: for an in-memory PDF…) is a plain href; only
// scheme-less strings and route objects are app routes. A `blob:` given to RouterLink resolves relative
// to the current route and 404s.
const urlSchemePattern = /^[a-z][a-z\d+.-]*:/i;
const isExternalLink = computed(() => typeof props.to === 'string' && urlSchemePattern.test(props.to));
</script>

<style scoped lang="scss">
a {
  font-size: inherit;
  line-height: inherit;
  text-decoration: none;

  &.decoration:hover {
    text-decoration: underline;
  }

  &.accent {
    color: var(--t-color-orange-500);
    font-weight: 600;
  }
}
</style>
